import { useState, Fragment, useMemo, useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import dayjs from "dayjs"
import { useQuery, useQueryClient } from "react-query"
import { LOAD } from "types/"
import {
  customerCustomDisplay,
  filterLoadsWithCustomerGroups,
  filterLoadsWithPhrase,
} from "utils/"
import Checkbox from "components/Checkbox"
import classNames from "classnames"
import ReactTooltip from "react-tooltip"
import useUserStore from "stores/user"
import { cn } from "lib/utils"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog"

import { Button } from "components/ui/button"
import { LegacyButton } from "components/Button/legacy-button-alias"
import { Ban, Check, Clock, RouteIcon, XIcon } from "lucide-react"
import ApproveLoadButton from "./ApproveLoadButton"
import FormItem from "../FormTable/FormItem"
import Chooser from "../Chooser"
import { useFlags } from "launchdarkly-react-client-sdk"
import { useAuth0 } from "@auth0/auth0-react"
import axios, { AxiosResponse } from "axios"
import { useTranslation } from "react-i18next"

const CONFIRMATION_STATUS = {
  pending: "Cus. Pend.",
  approved: "Mgr. Pend.",
  disapproved: "Cus. Rej.",
  rejected: "Cus. Rej.",
}

const groupMapper = (data: { id: number; name: string }[]) =>
  data.map((item) => ({
    value: item.id,
    label: item.name,
  }))

const getLoadLocations = (load: LOAD) => {
  if (load.hasMultipleDeliveries && load.hasMultipleLocations) {
    return load.deliveries.flatMap((l) =>
      l.products.map((_, i: number) => l.deliveryName)
    )
  }

  if (load.hasMultipleLocations) {
    return load.deliveries.flatMap((l) =>
      l.products.map((_, i: number) => l.location?.location_name)
    )
  }

  return [load.deliveries[0]?.location?.location_name].filter(Boolean)
}

const loadsMapper = (data: any) =>
  (data ?? []).map((load: any) => {
    load.deliveries = Object.values(load?.deliveries ?? {}).map(
      (deliveries: any) => {
        deliveries.products = Object.values(deliveries?.products ?? {})
        return deliveries
      }
    )
    load.pickups = Object.values(load?.pickups ?? {}).map((pickups: any) => {
      pickups.products = Object.values(pickups?.products ?? {})
      return pickups
    })
    return load
  })

type LOADS_SCHEMA = {
  label: string
  name: string
  placeholder?: string
  width?: string
  center?: boolean
  custom_display?: (
    load: LOAD,
    onSelect?: any,
    selected?: any[],
    approverPermission?: boolean
  ) => string | React.ReactNode
  noSort?: boolean
}

const loadsSchema: LOADS_SCHEMA[] = [
  {
    name: "check",
    label: "",
    noSort: true,
    custom_display: (load: LOAD, onSelect: any, selected?: any[]) => (
      <Checkbox
        label=""
        onChange={() => onSelect(load)}
        checked={selected?.includes(load)}
      />
    ),
  },
  {
    name: "id",
    label: "Load Info",
    placeholder: "Load Info",
    custom_display: (load: LOAD) => {
      const customerInfo = [customerCustomDisplay(load)]
        ?.filter(Boolean)
        ?.join("")
      return (
        <>
          <div className="w-full flex gap-1 mb-0.5">
            <Link
              target="_blank"
              to={`/new_loads/${load.id}`}
              className="text-primary-blue hover:text-primary-text inline-block"
            >
              #{load.id}
            </Link>
            <span
              data-tip
              data-for={`tooltip-load-${load.id}`}
              className="text-black inline-block truncate"
            >
              {customerInfo}
            </span>
          </div>
          {/* <div className="text-black">
            {getLoadLocations(load)?.map((location) => (
              <span className="block">{location}</span>
            ))}
          </div> */}
          <ReactTooltip
            place="top"
            effect="solid"
            id={`tooltip-load-${load.id}`}
          >
            <div className="text-white">{customerInfo}</div>
          </ReactTooltip>
        </>
      )
    },
  },
  {
    name: "confirmation_required",
    label: "Status",
    custom_display: (load: LOAD) => {
      if (!load?.confirmation_status) return null
      const isManagerRejected = load.review_status === 2
      const textKey =
        load.confirmation_status.toLowerCase() as keyof typeof CONFIRMATION_STATUS
      const text = isManagerRejected
        ? "Mgr. Rej."
        : CONFIRMATION_STATUS[textKey]
      if (!text) return null
      const color =
        textKey === "pending"
          ? "bg-amber-200"
          : textKey === "approved"
          ? "bg-green-200"
          : "bg-red-200"
      const icon =
        textKey === "pending" ? (
          <Clock className="w-4 h-4" />
        ) : textKey === "approved" ? (
          <Check className="w-4 h-4" />
        ) : (
          <Ban className="w-4 h-4" />
        )
      return (
        <div
          className={cn("flex items-center gap-1 p-2 py-1 rounded-xl", color)}
        >
          {icon}
          <span className="text-sm text-black">{text}</span>
        </div>
      )
    },
  },
  {
    name: "driver",
    label: "Driver",
    custom_display: (load: LOAD) => (
      <div
        key={`${load?.driver?.driver_name}_${load?.id}`}
        className="text-black  grid grid-cols-2"
      >
        {load?.driver?.driver_name ?? "Unassigned"}
      </div>
    ),
  },
  {
    name: "products",
    label: "Products",
    custom_display: (load: LOAD) => (
      <>
        {(
          (load?.deliveries?.length ? load?.deliveries : load.pickups) ?? []
        ).flatMap((l: any) =>
          (l?.products ?? []).map((e: any) => (
            <div key={e.product?.code} className="text-black  grid grid-cols-2">
              <span>{e.product?.code}</span>
              <span>{e.expected_gross_quantity} gal</span>
            </div>
          ))
        )}
      </>
    ),
  },
  {
    name: "deliver_to",
    label: "Delivery To",
    custom_display: (load: LOAD) => (
      <>
        {(load?.deliveries ?? []).map((delivery: any) => (
          <div className="text-black " key={delivery.id}>
            {[
              delivery?.customer?.company_name,
              delivery?.location?.location_name,
            ]
              .filter(Boolean)
              .join(" - ")}
          </div>
        ))}
      </>
    ),
  },
  {
    name: "suppliers",
    label: "Supply",
    custom_display: (load: LOAD) => {
      const output = [] as React.ReactNode[]
      ;(load?.pickups ?? []).map((l: any) =>
        (l?.products ?? []).map((e: any) => {
          const pref = e.preferences?.[0]

          const texts = []
          const locationArray = []
          const loadingNumberArray = []

          if (pref?.supplier?.name) locationArray.push(pref?.supplier?.name)
          if (pref?.terminal?.name) locationArray.push(pref?.terminal?.name)
          if (pref?.wholesaler) loadingNumberArray.push(pref.wholesaler)
          if (pref?.description) loadingNumberArray.push(pref.description)

          const locationText = locationArray.join(" @ ")
          const loadingNumberText = loadingNumberArray.join(" - ")
          if (locationText) texts.push(locationText)
          if (loadingNumberText) texts.push(loadingNumberText)

          return output.push(<div className="text-black ">{texts}</div>)
        })
      )
      return <>{output}</>
    },
  },
  {
    name: "delivery_window",
    label: "Delivery window",
    custom_display: (load: LOAD) => {
      const startTime =
        load?.delivery_start_time &&
        dayjs(load?.delivery_start_time).isValid() &&
        dayjs(load?.delivery_start_time).format("MM/DD HH:mm")
      const endTime =
        (load?.delivery_end_time &&
          dayjs(load?.delivery_end_time).isValid() &&
          dayjs(load?.delivery_end_time).format("MM/DD HH:mm")) ??
        ""
      return (
        <div
          key={`${load?.driver?.driver_name}_${load?.id}`}
          className="text-black grid"
        >
          <span> {startTime}</span>
          <span> {endTime}</span>
        </div>
      )
    },
  },
  {
    name: "margin",
    label: "Margin",
    custom_display: (load: LOAD) => {
      const output = [] as React.ReactNode[]
      const items = load?.deliveries ?? []
      items?.map((l) =>
        (l?.products ?? []).map((p) => {
          const rawMargin =
            p?.cost_basis && p?.price ? p?.price - p?.cost_basis : null
          const margin =
            rawMargin !== null ? parseFloat(rawMargin.toFixed(2)) : null
          output.push(
            <div
              key={p.id}
              className={cn(
                margin !== null && margin < 0 ? "text-red-500" : "text-black"
              )}
            >
              {margin === 0 ? 0 : margin !== null ? margin : "-"}
            </div>
          )
          return
        })
      )
      return <>{output}</>
    },
  },
  {
    name: "actions",
    label: "Actions",
    center: true,
    custom_display: (
      load: LOAD,
      onSelect?: any,
      selected?: any,
      approverPermission?: boolean
    ) => (
      <div className="w-full flex gap-1 mb-0.5 items-center justify-center">
        <ApproveLoadButton
          hide={!approverPermission}
          text="Approve"
          loads={[
            {
              id: load?.id,
              driver: load?.driver?.id,
              delivery_start_time: load?.delivery_start_time,
              delivery_end_time: load?.delivery_end_time,
            },
          ]}
        />
        <Link
          target="_blank"
          to={`/new_loads/${load.id}`}
          className="group cursor-pointer"
        >
          <i className="ml-2 ri-edit-fill text-black group-hover:text-primary-blue text-xl" />
        </Link>
      </div>
    ),
  },
]

export default () => {
  const { usePendingLoadsV2 } = useFlags()
  const { t } = useTranslation()
  const approverPermission = useUserStore((state) => state.approverPermission)
  const [searchPhrase, setSearchPhrase] = useState<string>("")
  const [customerGroups, setCustomerGroups] = useState<any>({})
  const [effectiveStartDate] = useState<any>(
    dayjs().subtract(1, "month").format("YYYY-MM-DD")
  )
  const [effectiveEndDate] = useState<any>(
    dayjs().add(1, "month").format("YYYY-MM-DD")
  )
  const [selectedLoads, setSelectedLoads] = useState<any>([])
  const [open, setOpen] = useState(false)
  const { isAuthenticated, isLoading: isLoadingAuth0 } = useAuth0()

  const delayedFetch = useMemo<Promise<
    () => Promise<AxiosResponse<any>>
  > | null>(() => {
    if (isLoadingAuth0 || !isAuthenticated) return null

    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(() =>
          axios.get("/event/loads/", {
            params: {
              start_date: effectiveStartDate,
              end_date: effectiveEndDate,
              only_pending: true,
            },
          })
        )
      }, 600)
    })
  }, [isLoadingAuth0, isAuthenticated, effectiveStartDate, effectiveEndDate])

  const queryClient = useQueryClient()
  const { data: loads, refetch: refetchLoad } = useQuery<LOAD[]>(
    ["event_loads_pending"],
    async () => {
      if (delayedFetch) {
        const fetchFn = await delayedFetch
        const { data } = await fetchFn()
        return loadsMapper ? loadsMapper(data) : data
      }
    },
    {
      refetchOnWindowFocus: open,
      enabled: !!delayedFetch,
    }
  )

  useEffect(() => {
    open && refetchLoad()
  }, [open])

  const formattedData = useMemo(() => {
    if (!loads) return []
    return loads?.map((load) => {
      const loadProducts =
        (load?.deliveries?.length ? load?.deliveries : load.pickups) ?? []
      const preferences = (load?.pickups ?? []).flatMap((l: any) =>
        (l?.products ?? []).map((e: any) => {
          const pref = e.preferences?.[0]
          return pref
        })
      )
      const suppliers = preferences
        ?.map((p) => p?.supplier?.name || p?.terminal?.name)
        .join(", ")
      return {
        ...load,
        products: loadProducts
          ?.map((l: any) =>
            (l?.products ?? []).map((e: any) => e.product?.code)
          )
          .join(", "),
        suppliers,
      }
    })
  }, [loads])

  const sortedAndFilteredData = useMemo(() => {
    if (!formattedData) return []

    const filteredData = formattedData
      ?.filter(filterLoadsWithPhrase(searchPhrase))
      ?.filter(filterLoadsWithCustomerGroups(customerGroups))

    return filteredData
  }, [formattedData, searchPhrase, customerGroups])

  const onSelectLoad = (load: any) => {
    setSelectedLoads((prev: any) => {
      const index = prev?.indexOf(load)
      if (index === -1) return [...prev, load]
      return prev.filter((l: any) => l?.id !== load?.id)
    })
  }

  const onSelectAll = () => {
    setSelectedLoads((prev: any) => {
      if (prev?.length === sortedAndFilteredData?.length) return []
      return sortedAndFilteredData
    })
  }

  const onClose = () => {
    queryClient.invalidateQueries(["calendar-loads"])
    setOpen(false)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {usePendingLoadsV2 ? (
          <Button
            className="flex items-center justify-start gap-2"
            variant={approverPermission ? "secondary" : "outline"}
          >
            <RouteIcon className="w-4 h-4" />
            {t("core.pendingLoads")}
            {Boolean(formattedData?.length) && (
              <span className="bg-black text-white font-semibold text-xs rounded-md py-0.5 px-1.5 flex items-center justify-center">
                {formattedData?.length}
              </span>
            )}
          </Button>
        ) : (
          <LegacyButton
            className="mr-2"
            type={approverPermission ? "" : "secondary"}
            text="Pending Loads"
            tag={formattedData?.length ? `${formattedData?.length}` : null}
            tagBg={approverPermission ? "bg-red-500" : undefined}
          />
        )}
      </DialogTrigger>
      <DialogContent
        className="bootstrap-scope [&>button]:hidden min-h-[60vh] max-h-[80vh] max-w-[90vw] overflow-scroll scrollbar-hide flex flex-col"
        onInteractOutside={onClose}
      >
        <DialogHeader className="text-left border-b px-0">
          <div className="flex items-center justify-between mb-1 gap-2">
            <DialogTitle className="mr-4 !text-base !text-primary-text">
              Pending Loads
            </DialogTitle>
            <FormItem
              className="w-52 h-9"
              placeholder="Search"
              name="seach"
              onChange={(e: any) => setSearchPhrase(e?.target?.value ?? "")}
            />
            <Chooser
              labelKey="name"
              placeholder="Customer Group"
              className="min-w-[210px]"
              selected={customerGroups}
              endpoint={`customer-groups/lite/?page_size=1000&ordering=name`}
              mapper={groupMapper}
              onChange={(e: { [key: number]: boolean }, item: any) =>
                setCustomerGroups(Object.keys(e ?? {}))
              }
            />
            <div className="flex flex-1 gap-1 justify-end">
              <ApproveLoadButton
                isBulk
                hide={!selectedLoads?.length || !approverPermission}
                loads={selectedLoads?.map((item: any) => ({
                  id: item?.id,
                  driver: item?.driver?.id,
                  delivery_start_time: item?.delivery_start_time,
                  delivery_end_time: item?.delivery_end_time,
                }))}
              />
              <button onClick={onClose}>
                <XIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </DialogHeader>
        <main className="grid grid-cols-[40px_auto_auto_auto_1fr_1fr_1.5fr_1fr_0.5fr_1fr] bootstrap-scope">
          {loadsSchema.map((schema) => (
            <div
              className={classNames(
                "inline-flex px-1.5 py-2 text-sm font-semibold bg-table-bg text-primary-text first-of-type:rounded-l-md last-of-type:rounded-r-md",
                {
                  "justify-center": schema.center,
                }
              )}
              key={schema?.label}
            >
              {schema?.name === "check" ? (
                <Checkbox
                  label=""
                  onChange={onSelectAll}
                  checked={
                    selectedLoads?.length &&
                    selectedLoads?.length === sortedAndFilteredData?.length
                  }
                />
              ) : (
                schema?.label ?? ""
              )}
            </div>
          ))}

          {(sortedAndFilteredData ?? []).map((load, index) => (
            <Fragment key={index}>
              {loadsSchema.map((schema) => (
                <div
                  className={classNames(
                    "px-1.5 py-1 my-1 first-of-type:rounded-l-md last-of-type:rounded-r-md",
                    selectedLoads?.includes(load) ? "bg-primary-bg" : "bg-white"
                  )}
                  key={`${index}-${schema.name}`}
                >
                  {schema.custom_display?.(
                    load,
                    onSelectLoad,
                    selectedLoads,
                    approverPermission
                  ) ?? load[schema.name as keyof typeof load]}
                </div>
              ))}
              <div className="col-span-full h-px bg-primary-bg" />
            </Fragment>
          ))}
        </main>
        {!(sortedAndFilteredData ?? []).length && (
          <div className="flex flex-1 col-span-full justify-center items-center">
            No Loads Found...
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
